import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import axios from 'axios';
import { useProfile } from '../context/ProfileContext';
import { SaveProfileInput } from '../api/profileApi';
import { membershipApi } from '../api/membershipApi';
import { Handedness, ProductOffering } from '../types';
import BrandLockup from '../components/BrandLockup';
import PromoBanner from '../components/PromoBanner';
import { usePromoCampaign } from '../hooks/usePromoCampaign';
import { clearPromoCampaign } from '../promoCampaign';
import { completeMobileReturn } from '../mobileReturn';
import { US_STATES } from '../constants/usStates';

/** Formats a subscription product's price as a short card label. */
const priceLabel = (product: ProductOffering): string =>
  product.price > 0
    ? `$${product.price % 1 === 0 ? product.price : product.price.toFixed(2)}/${product.billingInterval === 'Annual' ? 'yr' : 'mo'}`
    : 'Free';

const byDisplayOrder = (left: ProductOffering, right: ProductOffering): number =>
  left.displayOrder - right.displayOrder || left.name.localeCompare(right.name);

const HANDEDNESS_OPTIONS: { label: string; value: Handedness }[] = [
  { label: 'Right-handed', value: 'right' },
  { label: 'Left-handed', value: 'left' },
];

/**
 * New-account membership onboarding. A golfer selects a subscription plan and may redeem
 * a promo code. Profile details are optional and can be completed later from the main app.
 * A golfer who arrived through a promo campaign link (a QR code) sees the offer above the form and
 * starts its free trial instead of paying.
 */
const OnboardingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { profile, saveProfile, hasMembership, isHydrated, verification, logout } = useProfile();
  const {
    campaign: promo,
    loading: promoLoading,
    failed: promoFailed,
    refresh: refreshPromo,
    skip: skipPromo,
  } = usePromoCampaign(profile.promoCampaign);

  const [plans, setPlans] = useState<ProductOffering[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plan, setPlan] = useState<string | null>(profile.productOfferingId ?? null);
  const [name, setName] = useState(profile.name || user?.name || '');
  const [email, setEmail] = useState(profile.email || user?.email || '');
  const [state, setState] = useState(profile.state);
  const [handedness, setHandedness] = useState<Handedness>(profile.handedness ?? 'right');
  const [promoCode, setPromoCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mobileReturn = new URLSearchParams(location.search).get('mobileReturn');

  // Route gating: bounce unauthenticated users and skip onboarding once complete.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (!saving && isHydrated && hasMembership && verification?.isProfileConfirmed) {
      if (!completeMobileReturn(mobileReturn)) navigate('/setup', { replace: true });
    }
  }, [isLoading, isAuthenticated, isHydrated, hasMembership, mobileReturn, navigate, saving, verification?.isProfileConfirmed]);

  // Load subscription products in merchandising order and default new golfers to the free membership.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = (await membershipApi.getProducts()).sort(byDisplayOrder);
        if (cancelled) return;
        setPlans(catalog);
        const defaultProduct = catalog.find((product) => product.price > 0) ?? catalog[0];
        if (defaultProduct) setPlan((prev) => prev ?? defaultProduct.id);
      } catch {
        if (!cancelled) setError('We could not load the available plans. Please try again.');
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((candidate) => candidate.id === plan) ?? null,
    [plan, plans],
  );
  const rangeMembership = useMemo(
    () => plans.find((candidate) => candidate.price === 0) ?? null,
    [plans],
  );
  // Range stays in the catalog as the pre-checkout parking tier, but is not offered during onboarding.
  const visiblePlans = useMemo(() => plans.filter((candidate) => candidate.price > 0), [plans]);
  // The offer is for golfers who have not chosen a plan yet (the API turns it down for anyone else),
  // and it replaces checkout for the plan it covers: the API starts the free trial itself.
  const isNewGolfer = !profile.productOfferingId;
  const promoOffer = isNewGolfer ? promo : null;
  const promoApplies = Boolean(promoOffer && selectedPlan && selectedPlan.id === promoOffer.productOfferingId);
  // While the offer is being looked up, or when that failed, the golfer must not be able to slip into
  // the paid path by accident: they would be sent to Stripe for a plan they expected to be free.
  // That includes the moments before the profile loads, which is when a campaign recorded on the
  // account (by a different browser) becomes known.
  const promoUnresolved = isNewGolfer && (!isHydrated || promoLoading || promoFailed);
  const requiresCheckout = Boolean(
    selectedPlan
    && selectedPlan.price > 0
    && selectedPlan.id !== profile.productOfferingId
    && !promoApplies,
  );

  const handleSubmit = async () => {
    if (saving || plansLoading || promoUnresolved) return;
    if (plan === null) {
      setError('Choose a membership to continue.');
      return;
    }
    if (!name.trim() || !email.trim() || !state || !handedness) {
      setError('Enter your name, email, home state, and handedness to continue.');
      return;
    }
    if (selectedPlan && selectedPlan.price > 0 && !rangeMembership) {
      setError('The free Range Membership is not available right now. Please try again.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const input: SaveProfileInput = {
        name: name.trim(),
        email: email.trim(),
        state,
        handedness,
        caddieVoicePreference: profile.caddieVoicePreference,
        caddieTalkativeness: profile.caddieTalkativeness,
        caddieVoiceStyle: profile.caddieVoiceStyle,
        caddieRecommendationDepth: profile.caddieRecommendationDepth,
        caddiePrimaryFocus: profile.caddiePrimaryFocus,
        measurementSystem: profile.measurementSystem,
        // Keep the golfer on Range until Stripe confirms a paid Club checkout. A promo trial skips
        // checkout the same way: the API swaps Range for the campaign's plan, so a client that
        // predates campaigns can never be handed the paid plan without paying.
        productOfferingId: (requiresCheckout || promoApplies) && rangeMembership ? rangeMembership.id : plan,
        // The promo code is redeemed server-side: it grants Caddie Credits and (at launch) gates
        // onboarding for new golfers. It's the same field the API calls the trial/access code.
        trialCode: promoCode.trim() || undefined,
        promoCampaign: promoApplies && promoOffer ? promoOffer.slug : undefined,
      };
      await saveProfile(input);
      if (requiresCheckout && selectedPlan) {
        sessionStorage.setItem('caddiesight.checkout.returnTo', '/welcome');
        const checkoutUrl = await membershipApi.startCheckout(profile.userId, selectedPlan.id);
        window.location.assign(checkoutUrl);
        return;
      }
      if (promoApplies) clearPromoCampaign();
      if (!completeMobileReturn(mobileReturn)) navigate('/welcome', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const responseData = err.response.data;
        const problemDetails = typeof responseData === 'object' && responseData !== null
          ? responseData as { detail?: string; title?: string }
          : null;
        setError(typeof responseData === 'string'
          ? responseData
          : problemDetails?.detail ?? problemDetails?.title ?? 'We could not save your profile. Please try again.');
      } else {
        setError('Something went wrong saving your profile. Please try again.');
      }
      // If the offer is what the API turned down, this drops the banner so the next try is a normal sign-up.
      if (promoApplies) refreshPromo();
      setSaving(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div
        className="onboarding-page__scene"
        aria-hidden="true"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/onboarding-bg.jpg)` }}
      />
      <div className="onboarding-page__shade" aria-hidden="true" />

      <div className="onboarding-card">
        <div className="onboarding-card__brand">
          <BrandLockup tone="onLight" compact />
        </div>

        {promoOffer ? <PromoBanner campaign={promoOffer} /> : null}

        <div className="onboarding-card__header">
          <h1>Choose your membership</h1>
          <p>Select a plan or use a promo code to get on the course.</p>
        </div>

        <div className="onboarding-section">
          <h2>Golfer details</h2>
          <div className="onboarding-details-grid">
            <div className="onboarding-field">
              <label htmlFor="onboarding-name">Name</label>
              <InputText
                id="onboarding-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                autoComplete="name"
              />
            </div>
            <div className="onboarding-field">
              <label htmlFor="onboarding-email">Email</label>
              <InputText
                id="onboarding-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div className="onboarding-field">
              <label htmlFor="onboarding-state">Home state</label>
              <Dropdown
                inputId="onboarding-state"
                value={state}
                onChange={(event) => setState(event.value ?? '')}
                options={US_STATES}
                optionLabel="name"
                optionValue="code"
                placeholder="Select your state"
                filter
              />
            </div>
            <div className="onboarding-field">
              <label htmlFor="onboarding-handedness">Handedness</label>
              <SelectButton
                id="onboarding-handedness"
                value={handedness}
                onChange={(event) => event.value && setHandedness(event.value as Handedness)}
                options={HANDEDNESS_OPTIONS}
                optionLabel="label"
                optionValue="value"
                allowEmpty={false}
                className="onboarding-handedness"
              />
            </div>
          </div>
        </div>

        <div className="onboarding-section">
          {plansLoading ? (
            <div className="onboarding-plans-loading">
              <i className="pi pi-spin pi-spinner" aria-hidden="true" /> Loading memberships…
            </div>
          ) : (
            <div className={`onboarding-plans${visiblePlans.length === 1 ? ' onboarding-plans--single' : ''}`}>
              {visiblePlans.map((card) => {
                const active = plan === card.id;
                const onTrial = promoApplies && promoOffer?.productOfferingId === card.id;
                // The plan's own description quotes its own credits (50 for Club); a promo trial can grant
                // more, so say what the trial gives instead of contradicting it.
                const trialCredits = onTrial ? promoOffer?.caddieCredits ?? null : null;
                const featureText = trialCredits === null
                  ? card.description
                  : `Your trial includes ${trialCredits} Caddie Credits.`;
                return (
                  <button
                    key={card.id}
                    type="button"
                    className={`onboarding-plan${active ? ' onboarding-plan--active' : ''}`}
                    onClick={() => setPlan(card.id)}
                    aria-pressed={active}
                  >
                    <div className="onboarding-plan__head">
                      <span className="onboarding-plan__name">{card.name}</span>
                      {active ? (
                        <span className="onboarding-plan__selected">
                          <i className="pi pi-check" aria-hidden="true" /> Selected
                        </span>
                      ) : null}
                    </div>
                    <span className="onboarding-plan__price">
                      {onTrial && promoOffer ? `Free for ${promoOffer.trialDays} days` : priceLabel(card)}
                    </span>
                    <span className="onboarding-plan__feature" title={featureText}>
                      <i className="pi pi-check" aria-hidden="true" />
                      <span>{featureText}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="onboarding-section">
          <div className="onboarding-field">
            <label htmlFor="onboarding-promo">Promo code (optional)</label>
            <InputText
              id="onboarding-promo"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="e.g. CADDIE-AB12CD"
              autoComplete="off"
              autoCapitalize="characters"
            />
          </div>
        </div>

        {isNewGolfer && promoFailed && (
          <div className="onboarding-note onboarding-note--info" role="status">
            We couldn't check your free-trial offer right now.
            <span className="onboarding-note__actions">
              <button type="button" className="onboarding-link" onClick={refreshPromo}>Try again</button>
              <button type="button" className="onboarding-link" onClick={skipPromo}>Continue without it</button>
            </span>
          </div>
        )}

        {error && (
          <div className="onboarding-note onboarding-note--error" role="alert">
            {error}
          </div>
        )}

        <Button
          label={promoApplies ? 'Start my free trial' : 'Finish Setup'}
          icon={requiresCheckout ? 'pi pi-credit-card' : 'pi pi-flag'}
          className="onboarding-btn"
          disabled={saving || plansLoading || promoUnresolved}
          loading={saving}
          onClick={handleSubmit}
        />

        {requiresCheckout && (
          <p className="onboarding-hint">You'll complete payment securely with Stripe.</p>
        )}
        {promoApplies && promoOffer && (
          <p className="onboarding-hint">
            No payment needed. Your {promoOffer.trialDays}-day free trial starts today, and you can join Club
            Membership whenever you like.
          </p>
        )}

        <button type="button" className="onboarding-link" onClick={logout}>
          Log out and change user
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
